import React, { useEffect } from 'react';
import styled from 'styled-components';
import Helmet from 'react-helmet';
import {
  Card as MuiCard,
  CardContent,
  Divider as MuiDivider,
  Grid as MuiGrid,
  Typography,
  Box,
} from '@mui/material';
import { spacing } from '@mui/system';
import { Translate } from 'react-localize-redux';
import { useDispatch, useSelector } from 'react-redux';
import { getData } from '../../utils/index.jsx';
import { fetchProfileInfoAction } from '../../redux/actions/profileActions';
import ProfileDetailsCard from '../../components/profile/ProfileDetailsCard';
import SignatureUpload from '../../components/profile/SignatureUpload';
import useProfileSignature from '../../hooks/useProfileSignature';

const Divider = styled(MuiDivider)(spacing);
const Grid = styled(MuiGrid)(spacing);
const Card = styled(MuiCard)(spacing);

/**
 * Profile Page Component
 * 
 * Displays user profile information and settings:
 * - Left column: Profile details (name, UUID, avatar)
 * - Right column: Settings (profile picture, signature upload)
 */
const Profile: React.FC = () => {
  const dispatch = useDispatch();
  const currentInvestigation = useSelector((state: { investigations: { currentInvestigation?: { uuid: string } } }) => state.investigations?.currentInvestigation);
  const profileInfo = useSelector((state: { profile: { info?: { role?: { name: string } | null } } }) => state.profile?.info);

  // Get user data from localStorage
  const name = getData('name') || 'User';
  const surnames = getData('surnames') || '';
  const uuid = getData('uuid') || 'N/A';
  const userType = getData('type') || 'researcher';

  // Fetch investigation-scoped profile (includes role) when an investigation is selected
  useEffect(() => {
    if (currentInvestigation?.uuid) {
      dispatch(fetchProfileInfoAction(currentInvestigation.uuid));
    }
  }, [dispatch, currentInvestigation?.uuid]);

  // Use signature hook for managing signature state
  const {
    signature,
    loading: loadingSignature,
    uploading,
    error: signatureError,
    uploadSignature: handleUploadSignature,
    removeSignature: handleRemoveSignature,
  } = useProfileSignature();

  return (
    <React.Fragment>
      <Helmet title="Profile" />

      <Typography variant="h3" gutterBottom display="inline">
        <Translate id="profile.title" />
      </Typography>

      <Divider my={6} />

      <Grid container spacing={6}>
        {/* Left Column: Profile Details */}
        <Grid item xs={12} lg={4} xl={3}>
          <ProfileDetailsCard
            name={name}
            surnames={surnames}
            uuid={uuid}
            userType={userType as 'researcher' | 'hospital'}
            avatarUrl={null}
            role={currentInvestigation ? (profileInfo?.role ?? null) : null}
          />
        </Grid>

        {/* Right Column: Settings */}
        <Grid item xs={12} lg={8} xl={9}>
          <Typography variant="h5" gutterBottom>
            <Translate id="profile.settings" />
          </Typography>

          {/* Signature Upload Section */}
          {loadingSignature ? (
            <Card mb={6}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ✍️ <Translate id="profile.signature.title" />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <Translate id="general.loading.loading" />...
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box mb={6}>
              <SignatureUpload
                currentSignature={signature}
                onUpload={handleUploadSignature}
                onRemove={handleRemoveSignature}
                uploading={uploading}
                error={signatureError}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default Profile;

